import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, SimpleChanges, ViewChild } from "@angular/core";
import { ModalController, PopoverController } from "@ionic/angular";
import {
  MbscCalendarEvent,
  MbscCalendarColor,
  MbscCalendarMarked,
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventcalendar,
  MbscEventcalendarOptions,
  MbscEventcalendarView,
  MbscLocale,
  MbscPageChangeEvent,
  MbscPageLoadedEvent,
  MbscPageLoadingEvent,
  MbscSelectedDateChangeEvent,
  MbscSelectedEventsChangeEvent,
  locale,
} from "@mobiscroll/angular";
import { MbscEventDragEvent, MbscEventUpdateEvent } from "@mobiscroll/angular/dist/js/core/components/eventcalendar/eventcalendar.types.public";
import { TranslateService } from '@ngx-translate/core';
import { MultiSelectPopover } from "@omni/components/multi-select-popover/multi-select-popover";
import { ActivityService } from "@omni/services/activity/activity.service";
import { AuthenticationService } from "@omni/services/authentication.service";
import { Contact } from "@omni/classes/contact/contact.class";
import { ContactOfflineService } from "@omni/services/contact/contact.service";
import { AccountOfflineService } from "@omni/services/account/account.offline.service";
import { Account } from "@omni/classes/account/account.class";
import { DeviceService } from "@omni/services/device/device.service";
import { NavigationService, PageName } from "@omni/services/navigation/navigation.service";
import { UIService } from "@omni/services/ui/ui.service";
import { debounceTime, takeUntil } from "rxjs/operators";
import { Subject, Subscription } from "rxjs";
import { IActivityEvent, IActivityFilterEvent } from "@omni/interfaces/activity/activity-event.interface";
import { Activity, ActivityColorCode, ActivityType } from "@omni/classes/activity/activity.class";
import { FeatureActionsMap, IoConfigurationFeatureCodes } from "@omni/classes/authentication/user.class";
import { Appointment } from "@omni/models/appointment-data-model";
import { GlobalUtilityService } from "@omni/services/global-utility.service";
import { DateTimeFormatsService } from "@omni/services/date-time-formats/date-time-formats.service";
import { unsubscribeSubscriptionArray } from "@omni/utility/common.utility";
import { TimeOffService } from "@omni/services/time-off/time-off.service";
import { AppointmentActivity, CovisitorAccess } from "@omni/classes/activity/appointment.activity.class";
import { StoreCheckActivity } from "@omni/classes/activity/store-check.activity.class";
import { SampleActivity } from "@omni/classes/activity/sample.activity.class";
import { TimeOffActivity } from "@omni/classes/activity/timeoff.class";
import { PhoneActivity } from "@omni/classes/activity/phone.activity.class";
import { CaseActivity } from "@omni/classes/case-intake/case-activity.class";
import { EmailActivity, EmailActivityParty, EmailStatusCodes } from "@omni/classes/activity/email.activity.class";
import { Channel, ChannelActivityType, ChannelType } from "@omni/classes/consent/channel.class";
import { OrderActivity } from "@omni/classes/activity/order.activity.class";
import { Utility } from "@omni/utility/util";
import { ConsentService } from "@omni/services/consent/consent.service";
import { SetBookingActivity } from "@omni/classes/activity/set-booking.activity.class";
import { SecInfoConfigDataService } from "@omni/data-services/sec-info-config/sec-info-config-data-service";
import { SecondaryInfoEntityName } from "@omni/classes/sec-info-config/sec-info.class";
import { FOLLOW_UP_TYPE } from "@omni/classes/activity/follow-up-action.activity.class";
import { EventsService } from "@omni/services/events/events.service";
import { MedicalInquiry } from "@omni/classes/medical-inquiry/medical-inquiry.class";
import { cloneDeep, isEmpty, isEqual, orderBy } from 'lodash-es';
import { InitiateMeetingPayload, MeetingDataService, UpdateMeetingPayload } from "@omni/data-services/meeting/meeting.data.service";
import { NotificationService, ToastStyle } from "@omni/services/notification/notification.service";
import { ContactDragStateService } from "@omni/services/drag-drop/contact-drag-state.service";
import { AccountDragStateService } from "@omni/services/drag-drop/account-drag-state.service";
import { HapticsService } from "@omni/services/haptics/haptics.service";
import { CalendarColorService } from "@omni/services/calendar/calendar-color.service";
import { ActivityChipPopoverComponent, ActivityChipPopoverResult } from "./activity-chip-popover/activity-chip-popover.component";
import { DeltaService } from "@omni/data-services/delta/delta.service";

// Custom types
type CalendarViewTypes = 'Month' | 'Week' | '3 Days' | 'Day';
type CalendarViewTransition = 'day-3days' | 'day-week' | 'day-month' | '3days-day' | '3days-week' | '3days-month'
                              | 'week-day' | 'week-3days' | 'week-month' | 'month-day' | 'month-3days' | 'month-week';
type MbscCalendarViewTypes = 'month' | 'week';
type MbscSchedulerViewTypes = 'month' | 'week' | 'day';


@Component({
  selector: 'activity-calendar',
  templateUrl: 'activity-calendar.html',
  styleUrls: ['activity-calendar.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivityCalendarComponent {
  // Search text value input from parent/sibling
  @Input() calendarSearchText: string = '';
  // Event Calendar instance
  @ViewChild('calendar', { static: true }) calendar: MbscEventcalendar;

  @Output() onActivityClick = new EventEmitter<any>(); 

  currentViewType: CalendarViewTypes = 'Day';
  currentViewLabel: string = this.getViewLabel(this.currentViewType);

  skeletonActivities: MbscCalendarEvent[] = [];

  private readonly skeletonSlotsPerWeekday: readonly (readonly [number, number])[][] = [
    [[9, 240], [14, 180], [19, 120]],
    [[8, 180], [12, 240], [17, 180]],
    [[8, 180], [12, 180], [16, 240], [21, 120]],
    [[9, 300], [15, 180], [19, 180]],
    [[9, 240], [14, 180], [18, 240]],
    [[10, 240], [15, 180], [19, 150]],
    [[9, 180], [13, 240], [18, 180]],
  ];

  @HostListener('window:omni:sync:completed')
  @HostListener('window:omni:offlineDataLoad:Completed')
  onInitialSyncCompleted() {
    this.cd.markForCheck();
  }

  private buildSkeletonActivities(rangeStart: Date, rangeEnd: Date): void {
    if (this.deltaService.isInitialSyncFinished || !rangeStart || !rangeEnd) {
      this.skeletonActivities = [];
      return;
    }
    const placeholders: MbscCalendarEvent[] = [];
    const day = new Date(rangeStart);
    day.setHours(0, 0, 0, 0);
    while (day <= rangeEnd) {
      this.skeletonSlotsPerWeekday[day.getDay()].forEach(([startHour, durationMinutes], slotIndex) => {
        const start = new Date(day);
        start.setHours(startHour, 0, 0, 0);
        placeholders.push({
          id: `skeleton:${start.getTime()}:${slotIndex}`,
          start,
          end: new Date(start.getTime() + durationMinutes * 60000),
          editable: false,
          cssClass: 'omni-chip-skeleton',
          __skeleton: true,
        } as MbscCalendarEvent);
      });
      day.setDate(day.getDate() + 1);
    }
    this.skeletonActivities = placeholders;
    this.cd.detectChanges();
  }

  /**
   * ID of the activity currently open in the right pane, mirrored from
   * activityService.selectedActivity$. The chip whose id matches gets a
   * selection ring (see customActivityTemplate). Empty when nothing is open.
   */
  selectedActivityId: string = '';

  private readonly _orderSelectedFirst = (a: MbscCalendarEvent, b: MbscCalendarEvent): number => {
    const isSelectedA = !!this.selectedActivityId && a.id === this.selectedActivityId;
    const isSelectedB = !!this.selectedActivityId && b.id === this.selectedActivityId;
    if (isSelectedA !== isSelectedB) return isSelectedA ? -1 : 1;

    const weight = (event: MbscCalendarEvent): number => {
      const start = event.start ?? event.date;
      if (!start) return 0;
      return new Date(start as string | number | Date).getTime() * (event.allDay ? 1 : 10);
    };
    const weightDiff = weight(a) - weight(b);
    if (weightDiff) return weightDiff;
    return (a.title || a.text) > (b.title || b.text) ? 1 : -1;
  };

  selectedDate: Date = new Date();
  // This variable holds the current date that user intended to be.
  // selectedDate variable sometimes changes in unwanted way during
  // view/page transition due to two-way binding nature.
  // masterSelectionDate will be used to correct in such scenarios.
  masterSelectedDate: Date = new Date(this.selectedDate);

  // Event Calendar view options
  view: MbscEventcalendarView = {
    calendar: {
      type: this.getCalendarViewType(this.currentViewType),
      labels: false,
      popover: false,
    },
    schedule: {
      type: this.getSchedulerViewType(this.currentViewType),
      timeCellStep: 30,
      maxEventStack: this.getSchedulerViewType(this.currentViewType) == 'day' ? 'all' : 1
    },
  };

  // Event Calendar default options
  options: MbscEventcalendarOptions = {
    theme: 'ios',
    themeVariant: 'light',
    dragToMove: true,
    dragTimeStep: 30,
    eventOrder: this._orderSelectedFirst,
  };

  myLocale: MbscLocale;
  myTimeFormat: string = 'h A';
  allDayTextValue: string = this.translate.instant("ALL_DAY");

  // Activity data for the calendar
  activities: MbscCalendarEvent[] = [];
  activitiesForSearch: MbscCalendarEvent[] = [];
  marked: MbscCalendarMarked[] = [];
  // Mobiscroll re-renders its cells on every view/page change, so a class set on the DOM cell is lost.
  selectedSlotColors: MbscCalendarColor[] = [];

  viewOptionsPopoverHandle: any;
  viewOptionsPopoverData: any;

  private transition: CalendarViewTransition;
  private viewInTransition = false;
  private pageInTransition = false;

  private hasSuggestedMeetingsEnabled: boolean;
  private hasSuggestedWeComMessagesEnabled: boolean;
  private _subscriptions: Subscription[] = [];

  private isSearching: boolean = false;
  private isActivityEventHandler: boolean = false;
  @Output() newActivityOpened = new EventEmitter<boolean>();
  modelView;

  private lastVisibleStart: Date = null;
  private lastVisibleEnd: Date = null;
  private lastPageChangeDate: Date = null;
  private lastPageLoadingDate: Date = null;
  private lastOverflowTriggerEl: HTMLElement | null = null;
  private readonly ngDestroy$ = new Subject<boolean>();

  constructor(
    private readonly device: DeviceService,
    private readonly translate: TranslateService,
    private readonly authService: AuthenticationService,
    private readonly activityService: ActivityService,
    private readonly uiService: UIService,
    private readonly events: EventsService,
    private readonly popoverCtrl: PopoverController,
    protected readonly navService: NavigationService,
    public readonly utilityService: GlobalUtilityService,
    private readonly cd: ChangeDetectorRef,
    private readonly el: ElementRef,
    public readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly timeOffService: TimeOffService,
    private readonly consentService: ConsentService,
    private readonly modalCtrl: ModalController,
    private readonly secInfoConfigDataService: SecInfoConfigDataService,
    private readonly contactService: ContactOfflineService,
    private readonly meetingService: MeetingDataService,
    private readonly notificationService: NotificationService,
    private readonly contactDragState: ContactDragStateService,
    private readonly accountDragState: AccountDragStateService,
    private readonly accountService: AccountOfflineService,
    private readonly haptics: HapticsService,
    private readonly calendarColorService: CalendarColorService,
    public readonly deltaService: DeltaService,
  ) {
    this.setLocale();
    this.initViewOptionsPopoverData();
    this.allDayTextValue = this.translate.instant("ALL_DAY");
    this.myTimeFormat = this.dateTimeFormatsService.is12HourFormat ? 'h A' : 'HH';

    // Language change event subscription
    this.translate.onLangChange.pipe(
      debounceTime(100),
      takeUntil(this.ngDestroy$),
    ).subscribe(data => {
      if (data) {
        this.setLocale();
        this.initViewOptionsPopoverData();
        this.allDayTextValue = this.translate.instant("ALL_DAY");
      }
    });

    this.hasSuggestedMeetingsEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SHOW_SUGGESTED_MEETINGS);
    
    // Handle activity CRUD events
    this._subscriptions.push(
      this.activityService.activityEvent$
        .pipe(
          debounceTime(250),
        )
        .subscribe((event: IActivityEvent) => {
          if (event?.activity &&
            ((event.activity.type === ActivityType.Appointment
              && event.activity['indskr_suggest']
              && !this.hasSuggestedMeetingsEnabled)
              || (event.activity.type === ActivityType.Email
                && event.activity['indskr_suggest'] && !this.hasSuggestedWeComMessagesEnabled))) {
          } else {
            this._activityEventHandler(event);
          }
        })
      );

    // Mirror the currently-open activity so the matching calendar chip shows a
    // selection ring. Covers every entry point (calendar, list, background) and
    // clears when the pane closes.
    this._subscriptions.push(
      this.activityService.selectedActivity$.subscribe((activity: Activity) => {
        const newSelectedId = activity?.ID || '';
        if (newSelectedId !== this.selectedActivityId) {
          const previousSelectedId = this.selectedActivityId;
          this.selectedActivityId = newSelectedId;
          const affectsLoadedRange = this.activities.some(
            event => event.id === newSelectedId || event.id === previousSelectedId
          );
          if (affectsLoadedRange) {
            this.activities = [...this.activities];
          }
          this.cd.markForCheck();
        }
      })
    );

    // Handle activity data refresh event
    this._subscriptions.push(
        this.activityService.activityDataFilterEvent$
        .pipe(
          debounceTime(250),
        )
        .subscribe(async (event: IActivityFilterEvent) => {
          if (event.filterType === 'activityType' || event.filterType === 'teamView') {
            const isFilterCleared = event.filterType === 'activityType'
              && isEmpty(this.activityService.multiOptionActivityFilter)
              && this.activityService.activityFilter === ActivityType.AllActivity;
            if (isFilterCleared) {
              await this.reloadDataWithinRange(new Date());
              this.scrollToToday();
            } else {
              const startDate = this.dateTimeFormatsService.isValidDate(new Date(this.selectedDate)) ? new Date(this.selectedDate) : new Date();
              await this.reloadDataWithinRange(startDate);
            }
          }
        })
      );
      
    // Time format change event subscription
    this._subscriptions.push(
      this.dateTimeFormatsService.timeFormat.subscribe(tf => {
        if (tf) {
          this.myTimeFormat = this.dateTimeFormatsService.is12HourFormat ? 'h A' : 'HH';
          this.cd.markForCheck();
        }
      })
    );
  }


  /**
   *
   *  Init
   */
  private initViewOptionsPopoverData() {
    this.currentViewLabel = this.getViewLabel(this.currentViewType);
    this.viewOptionsPopoverData = [
      {
        text: '',
        expanded: true,
        value: this.currentViewType,
        items: [
          { text: this.getViewLabel('Day'), value: 'Day' },
          { text: this.getViewLabel('3 Days'), value: '3 Days' },
          { text: this.getViewLabel('Week'), value: 'Week' },
          { text: this.getViewLabel('Month'), value: 'Month' },
        ],
        handler: this.viewChangeHandler,
      }
    ];
  }

  ngOnInit() {
    // Do not re-add a host-level `--omni-chip-tint-alpha: 1` here: it reads `isActive`
    // too early and de-tints legacy-fallback chips too. global.scss does it per chip.

    // Wire onEventUpdate via options (not template event binding) so that
    // returning false is seen by Mobiscroll's _hook() and natively cancels
    // the move. Angular template bindings discard handler return values so
    // `return false` from a (onEventUpdate)="..." binding has no effect.
    this.options = { ...this.options, onEventUpdate: (args) => this.onEventUpdate(args) };
    this.timeOffService.setMobileTracker('week');
    this.updateViewType();
    this.events.observe('refreshAgenda')
      .pipe(
        takeUntil(this.ngDestroy$),
        debounceTime(250),
      ).subscribe(() => {
        this.reloadDataWithinRange(this.getReloadAnchorDate());
      });
      this.events.observe('sync:completed').pipe(takeUntil(this.ngDestroy$)).subscribe(() => {
        this.reloadDataWithinRange(this.getReloadAnchorDate());
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    try {
      if (changes) {
        if (changes.calendarSearchText
          && (changes.calendarSearchText.currentValue !== changes.calendarSearchText.previousValue)
        ) {
          setTimeout(() => {
            const searchTxt: string = changes?.calendarSearchText?.currentValue ?? '';
            this._searchByActivitySubject(searchTxt);
          }, changes.calendarSearchText.firstChange ? 200 : 0);
        }
      }
    }
    catch (err) {
      console.error(err);
    }
  }

  ngAfterViewInit() {
    this._attachDropBridge();
    // Capture the +N overflow trigger element on pointerdown (before Mobiscroll
    // opens its popup), so onEventClick can anchor the preview card to it.
    this.el.nativeElement.addEventListener('pointerdown', this._captureOverflowTrigger, true);
  }

  ngOnDestroy() {
    this._detachDropBridge();
    this.el.nativeElement.removeEventListener('pointerdown', this._captureOverflowTrigger, true);
    unsubscribeSubscriptionArray(this._subscriptions);
    this.ngDestroy$.next(true);
    this.ngDestroy$.complete();
  }

  public updateViewType(givenDate?: Date) {
    let targetDate: Date | undefined;
    if (this.device.isMobileDevice || this.uiService.showRightPane) {
      this.currentViewType = '3 Days';
      // Use the last clicked event date if available
      const cellData = this.uiService.cellData;
      if (givenDate || (cellData?.data?.startDate && !isNaN(new Date(cellData.data.startDate).getTime()) && this.calendar)) {
        targetDate = givenDate || new Date(cellData.data.startDate);
        // Set with the view, so Mobiscroll never renders a 3-day page without the target day.
        // The guard matters: the ResizeObserver calls this on every frame of the pane animation.
        if (new Date(this.selectedDate).getTime() !== targetDate.getTime()) {
          this.selectedDate = targetDate;
          this.masterSelectedDate = new Date(targetDate);
        }
        setTimeout(() => {
            this.selectedDate = targetDate;
            this.masterSelectedDate = new Date(targetDate);
            this.calendar.navigate(targetDate);
        }, 100);
      }
    } else {
      this.currentViewType = 'Week';
    }
    this.currentViewLabel = this.getViewLabel(this.currentViewType);
    this.view = {
      schedule: {
        type: this.getSchedulerViewType(this.currentViewType),
        timeCellStep: 30,
        maxEventStack: 1
      },
    };
    if (this.currentViewType === '3 Days') {
      this.view.schedule.size = this.getSchedulerViewSize(this.currentViewType);
    }
  }
  /**
   *
   *  Helpers
   */
  private setLocale() {
    const langCode = this.translate.currentLang === 'zh_CN' ? 'zh' : this.translate.currentLang;
    this.myLocale = locale[langCode];
  }
  // Gets supported view type option for the Calendar component of Event Calendar
  private getCalendarViewType(viewType: CalendarViewTypes): MbscCalendarViewTypes {
    switch (viewType) {
      case 'Month':
        return 'month';
      default:
        return 'week';
    }
  }
  // Gets supported view type option for the Scheduler component of Event Calendar
  private getSchedulerViewType(viewType: CalendarViewTypes): MbscSchedulerViewTypes {
    switch (viewType) {
      case 'Week':
        return 'week';
      case 'Month':
        return 'month';
      default:
        return 'day';
    }
  }
  // Gets view size option for the Scheduler component of Event Calendar
  private getSchedulerViewSize(viewType: CalendarViewTypes): 1 | 3 {
    return viewType === '3 Days' ? 3 : viewType === 'Day' ? undefined : 1;
  }
  // private isDateBetween(start: Date, end: Date, dateToCheck: Date) {
  //   if (
  //     start?.getTime() <= dateToCheck.getTime()
  //     && dateToCheck.getTime() < end.getTime()
  //   ) {
  //     return true;
  //   }
  //   return false;
  // }
  private getReloadAnchorDate(): Date {
    return this.dateTimeFormatsService.isValidDate(this.masterSelectedDate)
      ? new Date(this.masterSelectedDate)
      : new Date();
  }
  private isSameDate(date1: Date, date2: Date) {
    if (
      date1.getFullYear() === date2.getFullYear()
      && date1.getMonth() === date2.getMonth()
      && date1.getDate() === date2.getDate()
    ) {
      return true;
    }
    return false;
  }
  private getViewLabel(view: CalendarViewTypes): string {
    let label: string;
    switch (view) {
      case 'Day':
        label = this.translate.instant('DAY');
        break;
      case '3 Days':
        label = '3 ' + this.translate.instant('DAYS');
        break;
      case 'Week':
        label = this.translate.instant('WEEK');
        break;
      case 'Month':
        label = this.translate.instant('MONTH');
        break;
      default:
        break;
    }
    return label;
  }
  // Gets the CalendarViewTransition value that tells from where and to the view changes
  private getTransition(prev: CalendarViewTypes, curr: CalendarViewTypes): CalendarViewTransition {
    let transition: CalendarViewTransition;

    if (prev === 'Day') {
      switch (curr) {
        case '3 Days':
          transition = 'day-3days';
          break;
        case 'Week':
          transition = 'day-week';
          break;
        case 'Month':
          transition = 'day-month';
          break;
        default:
          break;
      }
    } else if (prev === '3 Days') {
      switch (curr) {
        case 'Day':
          transition = '3days-day';
          break;
        case 'Week':
          transition = '3days-week';
          break;
        case 'Month':
          transition = '3days-month';
          break;
        default:
          break;
      }
    } else if (prev === 'Week') {
      switch (curr) {
        case '3 Days':
          transition = 'week-3days';
          break;
        case 'Day':
          transition = 'week-day';
          break;
        case 'Month':
          transition = 'week-month';
          break;
        default:
          break;
      }
    } else if (prev === 'Month') {
      switch (curr) {
        case '3 Days':
          transition = 'month-3days';
          break;
        case 'Week':
          transition = 'month-week';
          break;
        case 'Day':
          transition = 'month-day';
          break;
        default:
          break;
      }
    }

    return transition;
  }
  private getMondayOfAdjacentWeek(d: Date, prev: boolean): Date {
    const daysSinceMonday = (d.getDay() + 6) % 7;
    return new Date(d.setDate(d.getDate() - daysSinceMonday + (prev ? -7 : 7)));
  }


  /**
   *
   *  Mobiscroll Event Calendar Events
   *  Refer to https://docs.mobiscroll.com/angular/eventcalendar
   */
  onPageChange(event: MbscPageChangeEvent) {
    const fromDay = event.firstDay;
    
    // Guard: Only process if the date has actually changed
    if (fromDay) {
      if (this.lastPageChangeDate && this.lastPageChangeDate.getTime() === fromDay.getTime()) {
        // Date hasn't changed, skip processing to prevent infinite loop
        return;
      }
      
      this.lastPageChangeDate = fromDay;
      // Keep the tapped slot's day while it is on the page; always a copy, readers call setHours on it.
      const slotStart = this.selectedSlotColors[0]?.start as Date | undefined;
      const pageHasSlot = !!slotStart && event.firstDay <= slotStart && slotStart < event.lastDay;
      this.uiService.selectedCalendarDateInWeekView = new Date(pageHasSlot ? slotStart : fromDay);
      this.events.publish('updatedDayClick');
    }

    if (!this.transition) {
      // Flag page changing within the same view
      this.pageInTransition = true;
    }
  }
  onPageLoading(event: MbscPageLoadingEvent) {
    const fromDay = event.firstDay;
    
    // Guard: Only process if the date has actually changed
    if (this.lastPageLoadingDate && this.lastPageLoadingDate.getTime() === fromDay.getTime()) {
      // Date hasn't changed, skip processing to prevent infinite loop
      return;
    }
    
    this.lastPageLoadingDate = fromDay;
    // Load data dynamically
    setTimeout(async () => {
      await this.reloadDataWithinRange(fromDay);
    });
  }

  onPageLoaded(event: MbscPageLoadedEvent) {
    this.onCalendarPageLoaded(event);
    // Flag view transition from / to Month view
    // to skip one extra onPageLoaded call before
    // updating selectedDate
    if (
      !this.viewInTransition
      && (
        this.transition?.includes('month-')
        || this.transition?.includes('-month')
      )
    ) {
      this.viewInTransition = true;
      if (this.currentViewType === "Day") {
        this._scrollToFirstActivity();
      }
      return;
    } else if (this.viewInTransition) {
      // Clear the flag
      this.viewInTransition = false;
    }

    // Page loading is done. Scroll to the time.
    if (this.transition) {
      // Page changed with the view change
      this.transition = undefined;
      if (this.currentViewType === "Day") {
        this._scrollToFirstActivity();
      } else {
        setTimeout(() => {
          const now = new Date();
          const newDate = new Date(this.masterSelectedDate);
          newDate.setHours(
            now.getHours(),
            now.getMinutes(),
            now.getSeconds(),
            now.getMilliseconds(),
          );
          this.selectedDate = newDate;
          this.masterSelectedDate = new Date(newDate);
        }, 100);
      }
    } else {
      // Page changed within current view
      if (this.pageInTransition) {
        this.pageInTransition = false;
        return;
      } else {
        this._scrollToFirstActivity();
      }
    }
  }
  onSelectedDateChange(event: MbscSelectedDateChangeEvent) {
  }
  onSelectedEventChange(event: MbscSelectedEventsChangeEvent) {
  }
  async onCellClick(event: MbscCellClickEvent) {
    let newDate = new Date(event.date);
    if (event.source === "timeline" || event.source === "schedule") {
      const endDate = this.dateTimeFormatsService.addMinutesToDate(new Date(event.date), 30);
      // Own copies, so a listener mutating cellData's dates can't move the highlight.
      this.selectedSlotColors = [{ start: new Date(newDate), end: new Date(endDate), cssClass: 'ind-mbsc-scheduler-selected-cell' }];
      this.cd.detectChanges();
      const targetCellDate = { startDate: newDate, endDate: endDate }
      this.uiService.cellData = { view: 'day-week-views', data: targetCellDate };
      this.uiService.selectedCalendarDateInWeekView = new Date(newDate);
      this.newActivityOpened.emit(true);
      this.uiService.showRightPane = true;
      // To avoid visual glitches - left pane width change and right pane change issues
      if (this.navService.getRootActiveChildNavViewPageName() === PageName.NothingSelectedView 
      && this.navService.getActiveChildNavViewPageName() === PageName.NewActivityComponent) {
        this.events.publish('updatedCellClick');
      } else {
        await this.navService.setChildNavRoot(PageName.NothingSelectedView, PageName.NothingSelectedView, PageName.GoAppHomeComponent);
        await this.navService.pushChildNavPageWithPageTracking(PageName.NewActivityComponent, PageName.NewActivityComponent, PageName.GoAppActivitiesPageComponent,
          {
            from: PageName.GoAppHomeComponent,
            callbackEvent: (isSuccess: boolean, isDestroyed: boolean) => this._handleNewActivityCallback(isSuccess, isDestroyed),
            targetDate: targetCellDate
          }).then(() => {
            this.events.publish('updatedCellClick');
          });
      }
    } else {
      if (this.currentViewType === 'Month') {
        // Clicked a date from the month view.
        // Set the default scroll time.
        newDate.setHours(8);
      } else {
        // Day, 3 Days, Week views
        if (this.isSameDate(newDate, this.selectedDate)) {
          // Ignore selecting same date
          return;
        }
        if (
          event.source === 'calendar'
          && this.currentViewType === 'Day'
        ) {
          //Required to reload data because all day activities are not displayed after selecting a date on the calendar
          const startDate = new Date(this.selectedDate);
          this.reloadDataWithinRange(startDate);
        }
      }
      // First, giving timeout for the selectedDate change
      setTimeout(() => {
        this.uiService.selectedCalendarDateInWeekView = new Date(newDate);
        // Giving another timeout for Month-to-Day view change
        setTimeout(() => {
          // Move into Day view if date cell clicked from Month view
          if (this.currentViewType === 'Month') {
            this.currentViewType = 'Day';
            this.currentViewLabel = this.getViewLabel(this.currentViewType);
            this.view = {
                schedule: {
                    type: 'day',
                    timeCellStep: 30,
                },
                calendar: {
                    type: this.getCalendarViewType(this.currentViewType),
                    labels: false,
                },
            };
            this.viewOptionsPopoverData[0].value = this.currentViewType;
            this.transition = this.getTransition('Month', this.currentViewType);
          }
          this.events.publish('updatedDayClick');
        });
      });
    }
  }

  private _handleNewActivityCallback(isSuccess: boolean, isDestroyed: boolean) {
    this.newActivityOpened.emit(false);
    // Back arrow only: a destroy also fires when the next slot click replaces the pane, after its highlight is set.
    if (!isSuccess && !isDestroyed) {
      this.selectedSlotColors = [];
      this.cd.detectChanges();
    }
    if (this.modelView) {
      this.modelView.dismiss();
      this.modelView = undefined;
    }
  }


  /**
   *
   *  Other Event Handlers
   */
  // Calendar view change handler
  private viewChangeHandler = async (selectedItem: { text: string, value: CalendarViewTypes }, popoverData: { value: string }) => {
    if (popoverData.value !== selectedItem.value) {
      popoverData.value = selectedItem.value;
      const prevViewType = this.currentViewType;
      this.currentViewType = selectedItem.value;
      this.currentViewLabel = selectedItem.text;
      this.transition = this.getTransition(prevViewType, this.currentViewType);

      setTimeout(() => {
        // Config and update the view option for the selected view
        if (this.currentViewType === 'Month') {
          // Month view
          this.view = {
            calendar: {
              type: 'month',
              labels: false,
              popover: false,
            },
          };
        } else {
          // Day, 3 Days, Week views
          if (this.currentViewType === 'Day') {
            const tempView: MbscEventcalendarView = {
              calendar: {
                type: this.getCalendarViewType(this.currentViewType),
                labels: false,
              },
              schedule: {
                type: this.getSchedulerViewType(this.currentViewType),
                timeCellStep: 30,
                maxEventStack: 'all'
              },
            };
            this.view = tempView;
          } else {
            // 3 Days and Week views have the scheduler only. 
            // If you use both a calendar and a scheduler, there is an issue with UI alignment.
            const tempView: MbscEventcalendarView = {
              schedule: {
                type: this.getSchedulerViewType(this.currentViewType),
                timeCellStep: 30,
                maxEventStack: 1
              },
            };
            if (this.currentViewType === '3 Days') {
              tempView.schedule.size = this.getSchedulerViewSize(this.currentViewType);
            }
            this.view = tempView;
          }

          // Record the current selectedDate as a reference
          // in case selectedDate changes in unwanted way during the view transition
          this.selectedDate = new Date(this.selectedDate);
          this.masterSelectedDate = new Date(this.selectedDate);
        }
        if (this.viewOptionsPopoverHandle) {
          this.viewOptionsPopoverHandle.dismiss();
        }
      });
    } else {
      if (this.viewOptionsPopoverHandle) {
        this.viewOptionsPopoverHandle.dismiss();
      }
    }
  };
  // Click activity on Scheduler view
  private readonly _captureOverflowTrigger = (e: PointerEvent): void => {
    // The visible "+N" pill is a normal schedule event carrying the
    // .mbsc-schedule-event-more class (singular). Do NOT match
    // .mbsc-schedule-events-more (plural) — that's the wrapper container which
    // spans the entire scrollable day grid (~1500px tall), so using it as the
    // popover anchor pins the card to the top of the viewport.
    const triggerEl = (e.target as Element)?.closest('.mbsc-schedule-event-more') as HTMLElement | null;
    if (triggerEl) this.lastOverflowTriggerEl = triggerEl;
  };

  async onEventClick(ev: MbscEventClickEvent) {
    this.selectedSlotColors = [];
    const selActivity = ev && ev.event;
    if (isEmpty(selActivity)) return;
    const selActivityId: string = selActivity?.id.toString() || '';
    // Placeholder chips from drag-to-create have no backing Activity — skip them.
    if (selActivityId.startsWith('placeholder:') || selActivityId.startsWith('skeleton:')) return;
    // Read the Activity reference stored on the chip at render time (OMNI-60512).
    // Avoids a service lookup that fails when the chip's offline_meeting_* id has
    // been replaced by a server GUID in activityService after sync.
    const activity: Activity = (selActivity as any).__activity;
    if (!activity) {
      console.warn('[OMNI-60512] chip missing __activity reference, id:', selActivityId);
      return;
    }

    // Only show the preview card popover for meetings, time-off, and procedure logs.
    // All other activity types open the details pane directly.
    const showsPreviewCard =
      activity.type === ActivityType.Appointment ||
      activity.type === ActivityType.TimeOff ||
      activity.type === ActivityType.TimeOffRequest ||
      activity.type === ActivityType.SurgeryOrder;

    // If the right-side details pane is already open, or this activity type
    // doesn't use a preview card, skip the popover and open directly.
    if (this.uiService.showRightPane || !showsPreviewCard) {
      this.uiService.cellData = { view: 'activity-day-week-views', data: { startDate: new Date(selActivity.startDate), endDate: new Date(selActivity.startDate) } };
      this.events.publish('selectedActivityChangedInBackround');
      this.events.publish('activity-pane:selectActivity', activity);
      this.onActivityClick.emit({ activity, isInboundFlow: false, isNewlyCreatedActivity: false, fetchData: false });
      return;
    }

    // Resolve from the Activity directly, as the chip mapping does, so the card cannot
    // show a different colour from the chip it opened out of. Don't read the colour off
    // the Mobiscroll event payload: it doesn't reliably preserve non-standard props.
    const calendarType = this.activityService.getActivityTypeForCalendarFilter(activity);
    const { fill: chipColor, ink: chipInk, inkOnCard: chipInkOnCard } =
      this.calendarColorService.resolve(activity, calendarType);

    // Determine the best anchor element for the Ionic popover.
    //
    // Two paths depending on ev.source:
    //   'popover'  — click came from Mobiscroll's +N overflow list. ev.target is
    //                the list-item inside the popup (not a calendar chip), and
    //                the popup is dismissed by the time our card renders. Anchor
    //                instead to the "+N" pill captured on pointerdown
    //                (lastOverflowTriggerEl), so the card opens next to it.
    //   anything else — click came directly from a calendar chip. ev.target is
    //                   the chip element (MbscEventClickEvent.target is documented
    //                   as "The DOM element of the clicked event").
    const isFromOverflowList = ev.source === 'popover';
    const anchorEl: HTMLElement = isFromOverflowList
      ? (this.lastOverflowTriggerEl ?? (ev.target as HTMLElement))
      : ((ev.target?.closest('.mbsc-schedule-event') as HTMLElement | null) ?? ev.target);

    const popover = await this.popoverCtrl.create({
      component: ActivityChipPopoverComponent,
      event: { target: anchorEl } as unknown as Event,
      componentProps: { activity, chipColor, chipInk, chipInkOnCard },
      cssClass: 'activity-chip-popover',
      showBackdrop: false,
    });
    await popover.present();
    const { data } = await popover.onDidDismiss<{ action: ActivityChipPopoverResult }>();
    if (data?.action !== 'open') return;

    this.uiService.cellData = { view: 'activity-day-week-views', data: { startDate: new Date(selActivity.startDate), endDate: new Date(selActivity.startDate) } };
    this.events.publish('selectedActivityChangedInBackround');
    this.events.publish('activity-pane:selectActivity', activity);
    this.onActivityClick.emit({ activity, isInboundFlow: false, isNewlyCreatedActivity: false, fetchData: false });
  }

  onEventDragStart(args: MbscEventDragEvent): void {
    if (args.action === 'move') {
      this.haptics.dragReady();
    }
  }

  onEventUpdate(args: MbscEventUpdateEvent): boolean | void {
    const activityId = args.oldEvent?.id as string;
    const oldStart   = args.oldEvent?.start as Date;
    const oldEnd     = args.oldEvent?.end as Date;
    const newStart   = args.event.start as Date;
    const newEnd     = args.event.end as Date;

    if (!activityId || !newStart || !oldStart || !oldEnd) return;
    if (new Date(newStart).getTime() === new Date(oldStart).getTime()) return;

    // Returning false from onEventUpdate is Mobiscroll's native cancel — it
    // reverts its own internal event store and DOM together. A plain early
    // return (or data-rebind + markForCheck/detectChanges) doesn't work on iPad:
    // Mobiscroll manages the drag DOM independently of Angular's [data] binding,
    // so the chip stays painted at the dropped slot regardless of what we write
    // to this.activities. Our data revert keeps the Angular model in sync so
    // there's no stale state if the user navigates away and back.
    const revertChip = () => {
      this.activities = this.activities.map(ev =>
        ev.id === activityId ? { ...ev, start: oldStart, end: oldEnd } : ev
      );
      this.cd.markForCheck();
      return false as const;
    };

    if (!this.activityService.validateMeetingDate(newStart)) {
      return revertChip();
    }

    const activity = this.activityService.activities.find(a => a.ID === activityId) as AppointmentActivity | undefined;
    if (!activity || activity.isCompleted) {
      return revertChip();
    }

    const coVisitorAccess = this.authService.user.buSettings?.['indskr_covisitoraccessonmeeting'];
    const isJointMeeting = !!(activity['isJointmeeting'] || activity['accompaniedUserList']?.length > 0);
    const isOwner = activity.ownerId === this.authService.user.systemUserID;
    if (isJointMeeting && !isOwner && coVisitorAccess !== CovisitorAccess.FULL_ACCESS) {
      return revertChip();
    }

    // Move chip to new slot immediately and show in-chip spinner while saving
    this.activities = this.activities.map(ev =>
      ev.id === activity.ID ? { ...ev, start: newStart, end: newEnd, __saving: true } : ev
    );
    this.cd.markForCheck();
    this._persistReschedule(activity, oldStart, oldEnd, newStart, newEnd);
  }

  private async _persistReschedule(
    activity: AppointmentActivity,
    oldStart: Date,
    oldEnd: Date,
    newStart: Date,
    newEnd: Date,
  ): Promise<void> {
    try {
      await this.meetingService.rescheduleAppointment(activity, newStart, newEnd);
    } catch {
      this.dateTimeFormatsService.updateLimitedMinTime.next({ startDateTime: oldStart, endDateTime: oldEnd });
      if (this.ngDestroy$.isStopped) return;
      // API failed — snap chip back to original position. detectChanges (not
      // markForCheck): this async continuation resumes outside Angular's zone
      // (onEventUpdate ran outside it), so on iPad's touch path no zone tick
      // follows and markForCheck's dirty flag never gets processed. detectChanges
      // forces the repaint immediately.
      this.activities = this.activities.map(ev =>
        ev.id === activity.ID ? { ...ev, start: oldStart, end: oldEnd, __saving: false } : ev
      );
      this.cd.detectChanges();
      this.notificationService.notify(
        this.translate.instant('RESCHEDULE_FAILED'),
        'Drag Reschedule', 'top', ToastStyle.DANGER,
      );
      return;
    }
    if (this.ngDestroy$.isStopped) return;
    // Success — clear spinner (calendar will re-render from activityService on publishActivityEvent)
    this.activities = this.activities.map(ev =>
      ev.id === activity.ID ? { ...ev, __saving: false } : ev
    );
    this.cd.markForCheck();
  }

  /** Imperative drop bridge for the Mobiscroll schedule view.
   *  Mobiscroll's externalDrop is bound to its own mbsc-draggable directive
   *  and doesn't pick up HTML5 native drags, so we attach our own dragover/
   *  drop on the calendar host, map (x,y) → (date,time) via the rendered DOM,
   *  and call the meeting persistence chain directly. */
  private _dropBridgeAttached = false;
  private _dragHoverEl: HTMLElement | null = null;
  // Guards against a second drop being processed while the meeting creation of a
  // prior drop is still in flight — prevents overlapping creates and a
  // reloadDataWithinRange race. Reset on every exit path of _createMeetingFromDrop.
  private _dropInFlight = false;
  private readonly _onCalendarDragOver = (event: DragEvent): void => {
    // Gate on app drag-state, not dataTransfer types: Android System WebView
    // hides the custom MIME type during dragover, so a types check there never
    // matches and preventDefault never runs — without which no drop event fires.
    if (
      !this.contactDragState.draggedContact &&
      !this.accountDragState.draggedAccount
    ) {
      return;
    }
    event.preventDefault();
    // 'move' (not 'copy') so the OS doesn't render a "+" cursor over the chip.
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move';
    this._highlightHoveredCell(event.clientX, event.clientY);
  };
  private readonly _onCalendarDragLeave = (event: DragEvent): void => {
    const next = event.relatedTarget as Node | null;
    if (next && this.el.nativeElement.contains(next)) return;
    this._clearHoverHighlight();
  };
  private readonly _onCalendarDrop = (event: DragEvent): void => {
    // The payload travels through app state (see ContactDragStateService /
    // AccountDragStateService), not dataTransfer — Android WebView strips the
    // custom MIME type even at drop.
    const accountPayload = this.accountDragState.draggedAccount;
    const contactPayload = this.contactDragState.draggedContact;
    if (!accountPayload?.accountId && !contactPayload?.contactId) return;

    event.preventDefault();
    event.stopPropagation();
    this._clearHoverHighlight();

    const slot = this._resolveDropSlot(event.clientX, event.clientY);
    if (!slot) {
      console.warn('[drag-to-calendar] could not resolve drop slot — only schedule views (Day / 3 Days / Week) accept drops');
      return;
    }

    // Pre-flight reject before adding any placeholder chip, so a rejected
    // drop leaves the calendar visually untouched and surfaces exactly one
    // toast. Both the contact and account branches inherit this gate.
    //
    // 1. Team-view active — drops would silently create meetings under the
    //    current user while viewing another rep's calendar, which is wrong.
    if (this.activityService.teamViewActive) {
      return;
    }
    // 2. Past-date / max-future-days. validateMeetingDate surfaces the right
    //    toast for the BU's configuration (strict RESTRICT_PAST_DATE flag vs.
    //    maxMeetingStartDays threshold) — we just consume its boolean.
    if (!this.activityService.validateMeetingDate(slot.start)) {
      return;
    }
    if (this._dropInFlight) {
      console.warn('[drag-to-calendar] a drop is already being processed — ignoring this drop');
      return;
    }
    this._dropInFlight = true;
    this.haptics.dropAccepted();

    if (accountPayload?.accountId) {
      const placeholderId = this._addAccountPlaceholderChip(
        accountPayload.accountId, accountPayload.accountName, slot.start, slot.end,
      );
      void this._createMeetingFromAccountDrop(
        accountPayload.accountId, accountPayload.accountName, slot.start, slot.end, placeholderId,
      );
      return;
    }

    const placeholderId = this._addPlaceholderChip(
      contactPayload.contactId, contactPayload.fullName, slot.start, slot.end,
    );
    void this._createMeetingFromDrop(
      contactPayload.contactId, contactPayload.fullName, slot.start, slot.end, placeholderId,
    );
  };

  private _attachDropBridge(): void {
    if (this._dropBridgeAttached) return;
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (!host) return;
    host.addEventListener('dragover', this._onCalendarDragOver);
    host.addEventListener('dragleave', this._onCalendarDragLeave);
    host.addEventListener('drop', this._onCalendarDrop);
    this._dropBridgeAttached = true;
  }

  private _detachDropBridge(): void {
    if (!this._dropBridgeAttached) return;
    const host = this.el?.nativeElement as HTMLElement | undefined;
    host?.removeEventListener('dragover', this._onCalendarDragOver);
    host?.removeEventListener('dragleave', this._onCalendarDragLeave);
    host?.removeEventListener('drop', this._onCalendarDrop);
    this._dropBridgeAttached = false;
    this._clearHoverHighlight();
  }

  /** Find the schedule-view cell under (x, y) and translate to a 30-min slot
   *  with 15-min snap. Returns null if the current view doesn't render a
   *  schedule grid (e.g. Month view). */
  private _resolveDropSlot(x: number, y: number): { start: Date; end: Date } | null {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    if (!el) {
      console.warn('[drag-to-calendar] elementFromPoint returned null', { x, y });
      return null;
    }
    const column = el.closest('.mbsc-schedule-column') as HTMLElement | null;
    if (!column) {
      console.warn('[drag-to-calendar] no .mbsc-schedule-column ancestor at drop point', el);
      return null;
    }
    const gridWrapper = column.closest('.mbsc-schedule-grid-wrapper') as HTMLElement | null;
    if (!gridWrapper) {
      console.warn('[drag-to-calendar] drop landed outside time grid (e.g. header / all-day)');
      return null;
    }
    const gridColumns = Array.from(
      gridWrapper.querySelectorAll('.mbsc-schedule-column'),
    ) as HTMLElement[];
    const dayIndex = gridColumns.indexOf(column);
    if (dayIndex < 0) {
      console.warn('[drag-to-calendar] column not found among grid columns', { dayIndex, total: gridColumns.length });
      return null;
    }

    // Compute the leftmost visible day from the view's reference date. Mobiscroll's
    // getViewDate() returns the *selected* date (mid-week in Week view, middle of 3
    // in 3-Days view), not the leftmost — so we derive leftmost ourselves based on
    // how many columns the grid is rendering.
    const reference = this.calendar?.getViewDate?.() ?? this.selectedDate ?? new Date();
    const columnCount = gridColumns.length;
    let dayShift = 0;
    if (columnCount === 7) {
      // Week view. firstDay=1 (Monday) per the calendar's [firstDay] binding.
      const dow = reference.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
      dayShift = -((dow + 6) % 7); // distance back to Monday
    } else if (columnCount === 3) {
      // 3-Days view = Mobiscroll scheduler `day` view, size 3, which anchors the
      // view date as the FIRST (leftmost) of the 3 days — so column 0 == reference.
      // (The earlier "middle column" assumption shifted every drop one day early.)
      dayShift = 0;
    } else if (columnCount === 1) {
      // Day view — reference is the only column.
      dayShift = 0;
    } else {
      console.warn('[drag-to-calendar] unexpected grid column count', columnCount);
      return null;
    }

    const targetDate = new Date(
      reference.getFullYear(),
      reference.getMonth(),
      reference.getDate() + dayShift + dayIndex,
      0,
      0,
      0,
      0,
    );

    // Time-of-day: y-distance from the column's first time-cell, scaled by
    // the cell's height (= minutesPerCell minutes per cell). The dropped
    // cell's sibling-index against the WHOLE PARENT was off because the
    // .mbsc-schedule-item parent groups cells across columns — scoping the
    // measurement to this column's first item avoids that entirely.
    const minutesPerCell = this.view?.schedule?.timeCellStep ?? 30;
    const itemsInColumn = Array.from(
      column.querySelectorAll('.mbsc-schedule-item'),
    ) as HTMLElement[];
    if (!itemsInColumn.length) {
      console.warn('[drag-to-calendar] no .mbsc-schedule-item inside dropped column');
      return null;
    }
    const firstItemRect = itemsInColumn[0].getBoundingClientRect();
    const cellHeight = firstItemRect.height || 1;
    const yFromFirstCell = y - firstItemRect.top;
    const minutesRaw = (yFromFirstCell / cellHeight) * minutesPerCell;
    // Snap to the start of the dropped cell — UX: dropping anywhere in the
    // 2:00–2:30 slot should land the chip at 2:00, not at the nearest 15-min
    // tick. Floor to cell granularity.
    const minutesSnapped = Math.max(0, Math.floor(minutesRaw / minutesPerCell)) * minutesPerCell;

    targetDate.setMinutes(minutesSnapped);
    const meetingDurationMs = (this.authService.user?.meeting_duration || 30) * 60 * 1000;
    const end = new Date(targetDate.getTime() + meetingDurationMs);
    return { start: targetDate, end };
  }

  /** Highlight the slot under the drop point. Uses elementFromPoint (the same
   *  resolution the actual drop uses) rather than event.target, so it tracks
   *  reliably under the mobile-drag-drop polyfill on iOS too. */
  private _highlightHoveredCell(x: number, y: number): void {
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    const cell = (el?.closest('.mbsc-schedule-item') ?? null) as HTMLElement | null;
    if (cell === this._dragHoverEl) return;
    this._clearHoverHighlight();
    if (cell) {
      cell.classList.add('omni-drop-hover');
      this._dragHoverEl = cell;
    }
  }

  private _clearHoverHighlight(): void {
    if (this._dragHoverEl) {
      this._dragHoverEl.classList.remove('omni-drop-hover');
      this._dragHoverEl = null;
    }
  }

  /** Add a local-only placeholder event on the calendar at the dropped slot.
   *  No persistence — the placeholder is wiped on next data reload or removed
   *  explicitly via _removePlaceholderChip on cancel/error. Returns the synthetic
   *  event id so the caller can remove this exact chip later. */
  private _addPlaceholderChip(contactId: string, fullNameHint: string | undefined, startDate: Date, endDate: Date): string {
    const contact: Contact | undefined = this.contactService.getContactByID(contactId);
    const fullName =
      fullNameHint ||
      contact?.fullName ||
      contact?.fullname ||
      'Customer';
    const placeholderId = `placeholder:${contactId}:${startDate.getTime()}`;
    const placeholder: MbscCalendarEvent & { __placeholder?: true; __contactId?: string; __saving?: boolean } = {
      id: placeholderId,
      title: fullName,
      text: fullName,
      start: startDate,
      end: endDate,
      color: ActivityColorCode.ActivityOpen,
      editable: false,
      __placeholder: true,
      __contactId: contactId,
      __saving: true,
    };
    this.activities = [...this.activities, placeholder as MbscCalendarEvent];
    this.cd.markForCheck();
    return placeholderId;
  }

  /** Add a local-only placeholder chip for an account drop. Mirrors the
   *  contact placeholder shape so the calendar render path treats it identically;
   *  the distinguishing tag is `__accountId` (vs `__contactId`). */
  private _addAccountPlaceholderChip(
    accountId: string,
    accountNameHint: string | undefined,
    startDate: Date,
    endDate: Date,
  ): string {
    const account = this.accountService.getAccountById(accountId);
    const accountName =
      accountNameHint ||
      account?.accountName ||
      'Account';
    const placeholderId = `placeholder:${accountId}:${startDate.getTime()}`;
    const placeholder: MbscCalendarEvent & {
      __placeholder?: true;
      __accountId?: string;
      __saving?: boolean;
    } = {
      id: placeholderId,
      title: accountName,
      text: accountName,
      start: startDate,
      end: endDate,
      color: ActivityColorCode.ActivityOpen,
      editable: false,
      __placeholder: true,
      __accountId: accountId,
      __saving: true,
    };
    this.activities = [...this.activities, placeholder as MbscCalendarEvent];
    this.cd.markForCheck();
    return placeholderId;
  }

  /** Remove a previously-added placeholder chip by its synthetic id. Used on
   *  cancel / error so an aborted drop leaves no orphan chip on the calendar. */
  private _removePlaceholderChip(placeholderId: string): void {
    const next = this.activities.filter(a => a.id !== placeholderId);
    if (next.length !== this.activities.length) {
      this.activities = next;
      this.cd.markForCheck();
    }
  }

  /** Create a real Appointment from a dropped contact chip at the resolved slot,
   *  with the contact attached, then refresh the calendar. On missing-contact /
   *  error, the placeholder chip is removed and nothing persists. */
  private async _createMeetingFromDrop(
    contactId: string,
    fullNameHint: string | undefined,
    start: Date,
    end: Date,
    placeholderId: string,
  ): Promise<void> {
    const contact: Contact | undefined = this.contactService.getContactByID(contactId);
    if (!contact) {
      this._dropInFlight = false;
      this._removePlaceholderChip(placeholderId);
      this.notificationService.notify(
        this.translate.instant('ERROR_FINDING_CONTACT_TO_ADD_TO_MEETING'),
        'Activity Calendar', 'top', ToastStyle.DANGER,
      );
      return;
    }

    const fullName = fullNameHint || contact.fullName || this.translate.instant('CONTACT');

    try {
      let subject = `${fullName} - ${this.translate.instant('MEETING')}`;
      if (this.authService.hasFeatureAction(FeatureActionsMap.MEETING_AUTO_SUBJECT)) {
        subject = `${fullName} - ${this.translate.instant('MEETING')} - ${this.authService.user?.displayName ?? ''}`;
      }
      const payload = new InitiateMeetingPayload(
        subject, '', start.getTime(), end.getTime(), '', this.activityService.generateOfflineMeetingId(),
      );
      payload.contacts = [contact];
      const response = await this.meetingService.createNewMeeting(payload);
      if (response instanceof AppointmentActivity && !this.meetingService.isErrorStartDateCreateMeeting) {
        this.contactService.addToRecentContacts(contact);
        const alreadyEmbedded = (response._raw_contactAttendees as any[])?.some(
          (r: any) => r.indskr_contactid === contact.ID
        );
        if (!alreadyEmbedded) {
          response.contacts.push(contact);
          const added = await this.meetingService.addContactsToMeeting(response, true);
          if (!added) {
            await this.activityService.upsertMeetingsOfflineData(response, true);
          }
        }

        // Auto-attach the contact's affiliated account, mirroring the
        // contact-tool's _handleCreateMeeting recipe (contact-details.ts):
        //   - Exactly one affiliated account → use it.
        //   - Multiple/zero affiliated, but the contact has a primaryAccount
        //     → pick the affiliated row matching primaryAccount.id.
        //   - Otherwise → no account attached.
        // When BU_APPOINTMENT_GA_HIDE_ACCOUNT_FIELD is enabled the account field
        // is hidden — skip auto-attachment to match every other code path
        // (new-activity.ts, contact-list.ts, updateMeetingWithContacts).
        const hideAccountField = this.authService.hasIoConfigWithFeatureCode(
          IoConfigurationFeatureCodes.BU_APPOINTMENT_GA_HIDE_ACCOUNT_FIELD,
        ) as boolean;
        const affiliatedAccounts = hideAccountField
          ? []
          : await this.accountService.getAffiliatedAccountsFromSelectedContactsForMeeting([contact]);
        let accountsToAttach: Account[] = [];
        if (affiliatedAccounts.length === 1) {
          accountsToAttach = affiliatedAccounts;
        } else if (contact.primaryAccount) {
          accountsToAttach = affiliatedAccounts.filter(af => af.id === contact.primaryAccount.id);
        }
        // Mirror contact-details._handleCreateMeeting: set location from the
        // contact's primary address when GPS check-in and account-address
        // population are both off. Call updateMeeting to persist the location
        // to Dynamics — just setting response.location in-memory is not enough.
        if (
          !this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_GPS_CHECK_IN) &&
          !this.authService.user?.buConfigs?.['indskr_populateaccountaddress']
        ) {
          const primaryAddress =
            contact.addressesList?.find((a) => a.isPrimary) ?? contact.addressesList?.[0];
          if (primaryAddress?.compositeAdd) {
            response.location = primaryAddress.compositeAdd;
            if (!this.device.isOffline && !this.activityService.hasOfflineMeetingData(response.ID)) {
              const locationPayload = new UpdateMeetingPayload(
                response.subject,
                response.location,
                response.scheduledStart,
                response.scheduledEnd,
              );
              await this.meetingService.updateMeeting(response, locationPayload);
            } else {
              await this.activityService.upsertMeetingsOfflineData(response);
            }
          }
        }

        if (accountsToAttach.length) {
          response['accounts'] = [...accountsToAttach];
          if (!this.device.isOffline && !this.activityService.hasOfflineMeetingData(response.ID)) {
            try {
              await this.meetingService.addAccountsToMeeting(response);
              if (!!this.authService.user?.buConfigs?.['indskr_populateaccountaddress']) {
                const selectedAddress = await this.accountService.getPrimaryAddressOfAccount(
                  response['accounts'][0]?.id ?? accountsToAttach[0].id,
                );
                if (selectedAddress) {
                  await this.accountService.addLocationToMeeting(response, selectedAddress);
                  const locationPayload = new UpdateMeetingPayload(
                    response.subject,
                    response.location,
                    response.scheduledStart,
                    response.scheduledEnd,
                    undefined, undefined, undefined, undefined, undefined,
                    response.indskr_meetinglocationlatitude,
                    response.indskr_meetinglocationlongitude,
                  );
                  await this.meetingService.updateMeeting(response, locationPayload);
                }
              }
            } catch (error) {
              // Account attach failure must not blow away the meeting create;
              // queue offline-pending so the next sync retries the link.
              console.error('[drag-to-calendar] affiliated account attach failed', error);
              await this.activityService.upsertMeetingsOfflineData(response, false, true);
            }
          } else {
            await this.activityService.upsertMeetingsOfflineData(response);
          }
        }

        // The configurable-summary (secondary-info) subject is built inside
        // createNewMeeting before the affiliated account is attached, so the
        // account-name segment is dropped (OMNI-60513). Re-derive it now that
        // the account is on the meeting, mirroring new-activity's
        // _handlePostCreationTasks recompute.
        await this._recomputeSecInfoSubjectAfterAttach(response);
      } else {
        this.notificationService.notify(
          this.translate.instant('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_MEETING'),
          'Activity Calendar', 'top', ToastStyle.DANGER,
        );
      }
      // Final local-DB flush: contacts and accounts may have been added after the
      // last individual PouchDB write. One upsert here ensures the local record
      // is complete (contacts + accounts + location) before the spinner dismisses.
      if (response instanceof AppointmentActivity) {
        await this.activityService.upsertMeetingsOfflineData(response);
      }
      // Remove the placeholder and refresh from source on every outcome — on
      // success the real meeting replaces it; on a non-Appointment response the
      // stale placeholder is still cleared.
      this._removePlaceholderChip(placeholderId);
      // Known gap: reloadDataWithinRange no-ops while a calendar search filter is
      // active, so a meeting created during an active search renders only once the
      // search is cleared.
      await this.reloadDataWithinRange(start);
    } catch (error) {
      console.error('[drag-to-calendar] meeting creation failed', error);
      this._removePlaceholderChip(placeholderId);
      this.notificationService.notify(
        this.translate.instant('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_MEETING'),
        'Activity Calendar', 'top', ToastStyle.DANGER,
      );
    } finally {
      this._dropInFlight = false;
    }
  }

  /** Create a real Appointment from a dropped account chip at the resolved slot,
   *  following the account-tool meeting-creation recipe (account attached via
   *  moveAccountToSelected + addAccountsToMeeting; primary address copied per
   *  BU config; single affiliated contact auto-attached). On missing-account /
   *  error, the placeholder chip is removed and nothing persists. */
  private async _createMeetingFromAccountDrop(
    accountId: string,
    accountNameHint: string | undefined,
    start: Date,
    end: Date,
    placeholderId: string,
  ): Promise<void> {
    const account = this.accountService.getAccountById(accountId);
    if (!account) {
      this._dropInFlight = false;
      this._removePlaceholderChip(placeholderId);
      this.notificationService.notify(
        this.translate.instant('ERROR_FINDING_ACCOUNT_TO_ADD_TO_MEETING'),
        'Activity Calendar', 'top', ToastStyle.DANGER,
      );
      return;
    }

    // Save silently in the background — no loader; the placeholder chip stands in
    // until reloadDataWithinRange swaps in the persisted meeting. Matches the
    // contact-drop UX (drag gestures shouldn't block on a modal loader).
    try {
      // Subject mirrors account-details._prepareMeetingPayload exactly — plain
      // 'Meeting' (or 'Visit' under VISIT_AUTO_SUBJECT). No name prefix.
      const isAcinoBaseUser = this.authService.hasFeatureAction(
        FeatureActionsMap.VISIT_AUTO_SUBJECT,
      );
      const subject = isAcinoBaseUser
        ? this.translate.instant('VISIT')
        : this.translate.instant('MEETING');

      const payload = new InitiateMeetingPayload(
        subject,
        '',
        start.getTime(),
        end.getTime(),
        '',
        this.activityService.generateOfflineMeetingId(),
        undefined,
      );
      const newMeeting = await this.meetingService.createNewMeeting(payload);

      if (
        newMeeting instanceof AppointmentActivity &&
        !this.meetingService.isErrorStartDateCreateMeeting
      ) {
        // Attach the account using the same handshake account-details uses:
        newMeeting['accounts'] = [account];

        if (!!this.authService.user?.buConfigs?.['indskr_populateaccountaddress']) {
          const selectedAddress = await this.accountService.getPrimaryAddressOfAccount(account.id);
          if (selectedAddress) {
            await this.accountService.addLocationToMeeting(newMeeting, selectedAddress);
          }
        }

        await this.activityService.upsertMeetingsOfflineData(newMeeting);

        if (!this.device.isOffline && !this.activityService.hasOfflineMeetingData(newMeeting.ID)) {
          await this.meetingService.addAccountsToMeeting(newMeeting);
        }

        // Affiliated-contact auto-attach: only when exactly one contact is
        // affiliated to this account (account-tool convention).
        const affiliated = await this.contactService.getAffiliatedContactsFromAccountsForMeeting([account]);
        if (affiliated.length === 1) {
          newMeeting['contacts'] = affiliated;
          await this.meetingService.addContactsToMeeting(newMeeting, true);

          // Mirror account-details._updateContactsOfSelectedMeeting: when GPS
          // check-in and account-address population are both off, fall back to
          // the affiliated contact's primary address (same condition, line 1113).
          if (
            newMeeting.location !== 'LiveMeet' &&
            !this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_GPS_CHECK_IN) &&
            !this.authService.user?.buConfigs?.['indskr_populateaccountaddress']
          ) {
            const primaryAddress =
              affiliated[0].addressesList?.find((a) => a.isPrimary) ??
              affiliated[0].addressesList?.[0];
            if (primaryAddress?.compositeAdd) {
              newMeeting.location = primaryAddress.compositeAdd;
              if (!this.device.isOffline && !this.activityService.hasOfflineMeetingData(newMeeting.ID)) {
                const locationPayload = new UpdateMeetingPayload(
                  newMeeting.subject,
                  newMeeting.location,
                  newMeeting.scheduledStart,
                  newMeeting.scheduledEnd,
                );
                await this.meetingService.updateMeeting(newMeeting, locationPayload);
              } else {
                await this.activityService.upsertMeetingsOfflineData(newMeeting);
              }
            }
          }
        }

        // The configurable-summary (secondary-info) subject is built inside
        // createNewMeeting before the account and its single affiliated contact
        // are attached, so both name segments are dropped (OMNI-60513). Re-derive
        // it now that they are on the meeting, mirroring new-activity's
        // _handlePostCreationTasks recompute.
        await this._recomputeSecInfoSubjectAfterAttach(newMeeting);
      } else {
        this.notificationService.notify(
          this.translate.instant('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_MEETING'),
          'Activity Calendar', 'top', ToastStyle.DANGER,
        );
      }
      // Final local-DB flush: contacts and accounts may have been added after the
      // last individual PouchDB write. One upsert here ensures the local record
      // is complete (contacts + accounts + location) before the spinner dismisses.
      if (newMeeting instanceof AppointmentActivity) {
        await this.activityService.upsertMeetingsOfflineData(newMeeting);
      }
      // Clear placeholder and refresh on every outcome — on success the real
      // meeting replaces it; on error the stale chip is still cleared.
      this._removePlaceholderChip(placeholderId);
      // Known gap: reloadDataWithinRange no-ops while a calendar search filter
      // is active, so a meeting created during an active search renders only
      // once the search is cleared.
      await this.reloadDataWithinRange(start);
    } catch (error) {
      console.error('[drag-to-calendar] account meeting creation failed', error);
      this._removePlaceholderChip(placeholderId);
      this.notificationService.notify(
        this.translate.instant('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_MEETING'),
        'Activity Calendar', 'top', ToastStyle.DANGER,
      );
    } finally {
      this._dropInFlight = false;
    }
  }

  /**
   * Re-derive and persist the configurable-summary (secondary-info) subject after
   * the affiliated account/contact have been attached to a drag-created meeting.
   *
   * createNewMeeting builds the secondary-info subject before those entities exist
   * on the meeting, so the account/contact name segments are silently dropped by
   * the formatter's filter(Boolean) — leaving subjects like "Appointments / [Call
   * Type] / @2026" (OMNI-60513). The account/contact tools avoid this because
   * new-activity's _handlePostCreationTasks (new-activity.ts) calls setMeetingSubject
   * a second time once everything is attached; the drag handlers had no equivalent.
   *
   * No-op unless appointment secondary-info is configured, and skips persistence
   * when the recomputed subject is unchanged.
   */
  private async _recomputeSecInfoSubjectAfterAttach(meeting: AppointmentActivity): Promise<void> {
    if (!this.secInfoConfigDataService.isAppointmentSecInfoConfigured) {
      return;
    }
    const recomputedSubject = this.meetingService.setMeetingSubject(meeting);
    if (!recomputedSubject || recomputedSubject === meeting.subject) {
      return;
    }
    meeting.subject = recomputedSubject;
    if (!this.device.isOffline && !this.activityService.hasOfflineMeetingData(meeting.ID)) {
      const subjectPayload = new UpdateMeetingPayload(
        meeting.subject,
        meeting.location,
        meeting.scheduledStart,
        meeting.scheduledEnd,
      );
      try {
        // updateMeetingType MUST be true — see meeting.data.service.ts's own
        // recomputeSecInfoSubjectAfterAttach for why: left false, a failed
        // PATCH is swallowed by GlobalErrorHandler instead of reaching this
        // catch, silently defeating the offline-retry safety net below.
        await this.meetingService.updateMeeting(meeting, subjectPayload, true);
      } catch (error) {
        // Subject patch failure must not blow away the meeting; queue offline-pending
        // so the next sync retries the subject update.
        console.error('[drag-to-calendar] secondary-info subject update failed', error);
        await this.activityService.upsertMeetingsOfflineData(meeting, false, true);
      }
    } else {
      await this.activityService.upsertMeetingsOfflineData(meeting);
    }
  }

  // Calendar view option selection component click handler - Day, 3 Days, Week, Month
  async onCalendarViewOptionBtnClick(event) {
    if (!this.viewOptionsPopoverHandle) {
      this.viewOptionsPopoverHandle = await this.popoverCtrl.create(
        {
          component: MultiSelectPopover,
          componentProps: {
            root: this.viewOptionsPopoverData
          },
          event
        }
      );

      await this.viewOptionsPopoverHandle.present();
      await this.viewOptionsPopoverHandle.onDidDismiss();
      this.viewOptionsPopoverHandle = null;
    }
  }
  // Left and right arrow navigation handler
  // Change 'selectedDate' by offset of each view type
  // to make the calendar to navigate to next/prev page
  navigatePage(prev: boolean): void {
    const currentDate = new Date(this.masterSelectedDate);
    if (this.currentViewType === 'Month') {
      const prevNextPage = new Date(currentDate.getFullYear(), currentDate.getMonth() + (prev ? -1 : 1), 1);
      this.selectedDate = prevNextPage;
      this.masterSelectedDate = new Date(this.selectedDate);
    } else {
      if (this.currentViewType === '3 Days') {
        const diff = currentDate.getDate() + (prev ? -3 : 3);
        const prevNextPage = new Date(currentDate.setDate(diff));
        this.selectedDate = prevNextPage;
        this.masterSelectedDate = new Date(this.selectedDate);
      } else {
        this.selectedDate = this.getMondayOfAdjacentWeek(currentDate, prev);
        this.masterSelectedDate = new Date(this.selectedDate);
      }
    }
    if(this.currentViewType != '3 Days') {
      this.uiService.selectedCalendarDateInWeekView = new Date(this.selectedDate);
      this.events.publish('updatedDayClick');
    }
    // The arrow button is Mobiscroll's mbsc-button inside its headerTemplate.
    // Mobiscroll emits its (click) output outside Angular's zone, so this
    // handler runs outside zone, the OnPush component never gets marked dirty,
    // and the new selectedDate doesn't propagate to mbsc-eventcalendar until
    // an unrelated zone-aware event (e.g. mousemove) forces a tick. Forcing CD
    // here pushes the binding through immediately.
    this.cd.detectChanges();
  }

  private async _activityEventHandler(event: IActivityEvent) {
    if (event?.action === 'Update') {
      if (event?.activity?.ID) {
        const newActivityObj: Appointment = this.formatActivityForCalendar(event.activity);
        if(!isEmpty(newActivityObj)) {
          this.isActivityEventHandler = true;
          const targetIndex = this.activities.findIndex(a=>a.id == newActivityObj.id);
          if(targetIndex > -1) {
            const scrollToTargetDate = newActivityObj.startDate;
            this.selectedDate = new Date(scrollToTargetDate);
            this.masterSelectedDate = new Date(scrollToTargetDate);

            const startDate = event.activity.startDate;
            await this.reloadDataWithinRange(startDate);
           
          } else {
          }
        }
      }
    } else if (event?.action === 'Create') {
      if (event?.activity) {
        const target: MbscCalendarEvent = await this._getActivityById(event.activity.ID);
        if (!target) {
          this.isActivityEventHandler = true;
          const startDate = event.activity.startDate;
          await this.reloadDataWithinRange(startDate);
        } else {
        }
        //show activity details after creating the activity      
        if (this.activityService.isNewActivityCreated && this.activityService.selectedActivity) {
          this.activityService.isNewActivityCreated = false;
          this.events.publish('activity-pane:selectActivity', this.activityService.selectedActivity);
        }
      }
    } else if (event?.action === 'Delete') {
      if (event?.activity?.ID) {
        const foundActivityObj: MbscCalendarEvent = await this._getActivityById(event.activity.ID);
        if (foundActivityObj) {
          this.isActivityEventHandler = true;
          const startDate = event.activity.startDate;
          await this.reloadDataWithinRange(startDate);
        } else {
        }
      }
    }
  }

  private async _getActivityById(id: string): Promise<MbscCalendarEvent> {
    const foundActivity = this.activities.find(a =>a.id == id) || undefined;
    return foundActivity;
  }

  private async _getDataWithinRange(curDate: Date = new Date()): Promise<Appointment[]> {
    // - Data loading range
    // - from: cur month first day - 6 weeks or cur date - 8 weeks, which ever is earlier
    // - to: cur next month first day + 6 weeks or cur date + 8 weeks, which ever is later
    const curMonthFirstDay: Date = new Date(curDate);
    curMonthFirstDay.setDate(1);
    curMonthFirstDay.setHours(0, 0, 0, 0);
    const nextMonthFirstDay: Date = this.dateTimeFormatsService.addMonthsToDate(curMonthFirstDay, 1);

    const curDateMinus8Weeks: Date = this.dateTimeFormatsService.subtractWeeks(curDate, 8);
    const curMonthFirstDayMinus6Weeks: Date = this.dateTimeFormatsService.subtractWeeks(curMonthFirstDay, 6);
    const curDatePlus8Weeks: Date = this.dateTimeFormatsService.addWeeksToDate(curDate, 8);
    const nextMonthFirstDayPlus6Weeks: Date = this.dateTimeFormatsService.addWeeksToDate(nextMonthFirstDay, 6);
  
    let dataRangeFrom = curDateMinus8Weeks.getTime() < curMonthFirstDayMinus6Weeks.getTime()
      ? curDateMinus8Weeks : curMonthFirstDayMinus6Weeks;
    let dataRangeTo = curDatePlus8Weeks.getTime() > nextMonthFirstDayPlus6Weeks.getTime()
      ? curDatePlus8Weeks : nextMonthFirstDayPlus6Weeks;
  
    return await this.getCalendarDataInDateRange(dataRangeFrom, dataRangeTo);
  }

  async reloadDataWithinRange(startDate: Date) {
    if(this.isSearching) return;
    const fromDay = new Date(startDate);
    this.activities = await this._getDataWithinRange(fromDay);
    this.activitiesForSearch = cloneDeep(this.activities);
    // detectChanges (not markForCheck): Mobiscroll fires onPageLoading from its
    // internal scheduler, which runs outside NgZone. markForCheck only flags dirty
    // and relies on a zone tick that never comes until the user moves the mouse,
    // so freshly-loaded chips stay invisible after week navigation. Synchronous
    // CD on this subtree is zone-agnostic and avoids a full app tick.
    this.cd.detectChanges();
  }

  // private _binarySearchDates(dateToCheck: Date): boolean {
  //   let leftIndex = 0;
  //   let rightIndex = this.activities.length - 1;

  //   while (leftIndex <= rightIndex) {
  //     const midIndex = Math.floor((leftIndex + rightIndex) / 2);
  //     const midDate = this.activities[midIndex]['startDate'];
  //     if (midDate.getTime() === dateToCheck.getTime()) {
  //       return true;
  //     } else if (midDate.getTime() < dateToCheck.getTime()) {
  //       leftIndex = midIndex + 1;
  //     } else {
  //       rightIndex = midIndex - 1;
  //     }
  //   }
  //   return false;
  // }


  // search function
  private async _searchByActivitySubject(searchTxt: string) {
    this.activities = cloneDeep(this.activitiesForSearch);
    if(!isEmpty(searchTxt)) {
      this.isSearching = true;
      let tempActivites = cloneDeep(this.activities);
      tempActivites = tempActivites.filter(activity => {
        let text:string = searchTxt.trim().toLocaleLowerCase();
        let str:string = '';
        if(activity.text){
          str = activity.text.trim().toLowerCase();
        }
        //ToDO: search in the other secondary info??
        if(activity.location){
          str = str + ' ' + activity.location.trim().toLowerCase();
        }
        return str.includes(text);
      });

      this.activities = tempActivites;
      this._scrollToFirstActivity();
      
    }else {
      this.isSearching = false;
      const startDate = this.dateTimeFormatsService.isValidDate(new Date(this.selectedDate)) ? new Date(this.selectedDate) : new Date();
      await this.reloadDataWithinRange(startDate);
    }
    this.activityService.activitiesSearchText = searchTxt;
  }

  private _scrollToFirstActivity() {
    //this.calendar.eventMap -- check event on the specific date
    //3day -- the range can be determined by this.calendar._lastDay - 3
    //weekday -- the range can be determined by this.calendar._lastDay - 7
    if (!this.isActivityEventHandler) {
      if (this.currentViewType === "Day") {
        let targetDateToScroll: Date = new Date(this.selectedDate) || new Date();
        let filterItemsForScroll = cloneDeep(this.activities) || [];
  
        filterItemsForScroll = filterItemsForScroll.filter(rawData => {
          const checkDate = new Date(rawData.start as Date);
          return !rawData.allDay && this.dateTimeFormatsService.isSameDayCheck(checkDate, this.selectedDate);
        });

        const isSelectedToday: boolean = this.dateTimeFormatsService.isTodayDate(this.selectedDate);
  
        if(isSelectedToday) {
          setTimeout(() => {
            const now = new Date();
            const newDate = new Date(this.selectedDate);
            newDate.setHours(
              now.getHours(),
              now.getMinutes(),
              now.getSeconds(),
              now.getMilliseconds(),
            );
            this.selectedDate = newDate;
            this.masterSelectedDate = new Date(newDate);
          }, 100);
        } else if (!isEmpty(filterItemsForScroll)) {
          // Set the first activity scroll time.
          filterItemsForScroll = orderBy(filterItemsForScroll, [d=>d.startDate.getHours(), d=>d.startDate.getMinutes()], 'asc');
          targetDateToScroll = filterItemsForScroll[0].startDate;
          setTimeout(() => {
            this.selectedDate = new Date(targetDateToScroll);
            this.masterSelectedDate = new Date(targetDateToScroll);
          },100);
        } else {
          if (!this.isSearching) {
            const now = new Date();
            targetDateToScroll.setHours(8);
            setTimeout(() => {
              this.selectedDate = new Date(targetDateToScroll);
              this.masterSelectedDate = new Date(targetDateToScroll);
            },100);
          }
        }
      } else if (this.currentViewType === "3 Days" || this.currentViewType === "Week") {
        //ToDO: scroll handling
      }
    } else {
      // keep tracking the current updated activity
      this.isActivityEventHandler = false;
      if (this.dateTimeFormatsService.isValidDate(this.selectedDate)) {
        const targetDateToScroll: Date = new Date(this.selectedDate);
        setTimeout(() => {
          this.selectedDate = new Date(targetDateToScroll);
          this.masterSelectedDate = new Date(targetDateToScroll);
          this.calendar.navigate(targetDateToScroll);
        },100);
      }
    }
  }

  /**
   * Returns the Appointment objects for the selected date range (For Calendar)
   */
  async getCalendarDataInDateRange(from: Date, to: Date): Promise<Appointment[]> {
    const appointments: Appointment[] = [];
    const fromTimestamp: number = from?.getTime();
    const toTimestamp: number = to?.getTime();
    const hasSuggestedMeetingsEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SHOW_SUGGESTED_MEETINGS);
    const hasSuggestedWeComMessagesEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SHOW_SUGGESTED_WECOM_MESSAGES);
    const hasSuggestedAlertsEnabled = this.authService.hasFeatureAction(FeatureActionsMap.SHOW_SUGGESTED_TASKS);
    
    this.activityService.filterDisplayActivity();
    
    for (let i = 0; i < this.activityService.displayActivities.length; i++) {
      const activity: Activity = this.activityService.displayActivities[i];
      const start: number = activity?.scheduledStart?.getTime();
      const noEnd: boolean = !this.dateTimeFormatsService.isValidDate(activity?.scheduledEnd);
      const end: number = activity?.scheduledEnd?.getTime();

      if (
        (start >= fromTimestamp && start <= toTimestamp)
        || (start < fromTimestamp && (noEnd || end > fromTimestamp))
      ) {
        let mappedObject;

        if (
          activity
          && (
            (
              activity.type === ActivityType.Appointment
              && (
                (
                  activity['indskr_suggest']
                  && !hasSuggestedMeetingsEnabled
                )
                || (activity as AppointmentActivity).indskr_parentcallid
              )
            )
            || (
              activity.type === ActivityType.Email
              && activity['indskr_recommendedmessage']
              && !hasSuggestedWeComMessagesEnabled
            )
            || (
              activity.type === ActivityType.FollowUp
              && activity['planType'] == FOLLOW_UP_TYPE.SUGGESTED
              && !hasSuggestedAlertsEnabled
            ) 
            || (
              activity.type === ActivityType.FollowUp
              && activity['planType'] == FOLLOW_UP_TYPE.SUGGESTED
              && activity.state == 2 && activity.status == 6
            )
          )
        ) {
        } else {
          mappedObject = this.formatActivityForCalendar(activity);
          if(mappedObject){
            appointments.push(mappedObject);
          }
        }
      }
    }

    return appointments;
  }

  formatActivityForCalendar(activity: Activity) {
    if (activity) {
      let endTime = activity.scheduledEnd;

      let mappedObject: Appointment = {
        id: activity.ID,
        text: this.getActivitysubject(activity),
        type: this.activityService.getActivityTypeForCalendarFilter(activity),
        location: activity.location && activity.location != 'No Location' ? activity.location : this.activityService.getNoLocationDefaultMessage(activity),
        start: activity.scheduledStart,
        startDate: activity.scheduledStart,
        end: endTime,
        endDate: activity.scheduledEnd,
        timeOffReason: activity['reason'] ? activity['reason'] : '',
        allDay: activity.allDayEvent && this.dateTimeFormatsService.isFutureDate(new Date(activity.scheduledEnd)),
        state: activity.state,
        info: activity.info,
        tooltip: this.getActivitysubject(activity),
      };
      // Fill and ink come from one resolver, which is what keeps them in step.
      const resolved = this.calendarColorService.resolve(activity, mappedObject.type);
      mappedObject.color = resolved.fill;
      mappedObject.ink = resolved.ink;
      mappedObject.inkOnCard = resolved.inkOnCard;
      mappedObject.editable = false; // default non-draggable; Appointment block overrides for open meetings

      if (activity.type === ActivityType.Appointment) {
        mappedObject.text = isEqual(mappedObject.text, "Meeting") ? this.translate.instant("MEETING") : mappedObject.text;
        if (this.authService.hasFeatureAction(FeatureActionsMap.VISIT_AUTO_SUBJECT)) {
          mappedObject.text = isEqual(mappedObject.text, "Visit") ? this.translate.instant("VISIT") : mappedObject.text;
        }
        mappedObject.location = isEqual(mappedObject.location, "No Location") ? this.translate.instant("NO_LOCATION") : mappedObject.location;
        mappedObject.proximityWarn = activity?.closeByActivityIds?.size > 0;
        mappedObject.recommended = activity['indskr_suggest'];
        const coVisitorAccess = this.authService.user.buSettings?.['indskr_covisitoraccessonmeeting'];
        const isJointMeeting = !!(activity['isJointmeeting'] || activity['accompaniedUserList']?.length > 0);
        const isOwner = activity.ownerId === this.authService.user.systemUserID;
        const isReadOnlyJoint = isJointMeeting && !isOwner && coVisitorAccess !== CovisitorAccess.FULL_ACCESS;
        mappedObject.editable = !activity.isCompleted
          && !this.activityService.teamViewActive
          && !activity.isFromXperiences
          && !isReadOnlyJoint;
        const startDateTimeValue: Date = new Date(activity.scheduledStart);
        const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
        mappedObject.startTime = meetingStartTime;
        const endDateTimeValue: Date = new Date(activity.scheduledEnd);
        const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
        mappedObject.endTime = meetingEndTime;
      }
      else if ( activity.type === ActivityType.StoreCheck) {
        const storeCheckActivity = activity as StoreCheckActivity;
        mappedObject.text = mappedObject.text;
        mappedObject.location = isEmpty(storeCheckActivity.categoryId) ? this.translate.instant('XPERIENCES_RELATIONSHIP_NO_CATEGORY') : storeCheckActivity.categoryName;
        const startDateTimeValue: Date = new Date(activity.scheduledStart);
        const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
        mappedObject.startTime = meetingStartTime;
        const endDateTimeValue: Date = new Date(activity.scheduledEnd);
        const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
        mappedObject.endTime = meetingEndTime;
      }
      let isActivityPartOfMultiSelectFilter:boolean = false;
      if(this.activityService.multiOptionActivityFilter && this.activityService.multiOptionActivityFilter.length > 0){
        isActivityPartOfMultiSelectFilter = this.activityService.multiOptionActivityFilter.some(a=>a.value == ActivityType.AllActivity) || this.activityService.multiOptionActivityFilter.some(a=> a.value == activity.type) || ((activity.type == ActivityType.TimeOffRequest && this.activityService.multiOptionActivityFilter.some(a=> a.value == 'TimeOff'))) || (activity instanceof AppointmentActivity && activity.isJointmeeting && this.activityService.multiOptionActivityFilter.some(a=> a.value == ActivityType.JointMeeting)) || (activity instanceof AppointmentActivity && activity.isRemoteDetailing && this.activityService.multiOptionActivityFilter.some(a=> a.value == ActivityType.RemoteMeeting)) || (activity instanceof AppointmentActivity && activity.location == 'LiveMeet' && this.activityService.multiOptionActivityFilter.some(a=> a.value == ActivityType.LiveMeet)) || (activity instanceof PhoneActivity && activity.jointphonecall && this.activityService.multiOptionActivityFilter.some(a=> a.value == ActivityType.JointPhoneCall))
      }
      if (this.activityService.activityFilter == ActivityType.AllActivity || activity.type === this.activityService.activityFilter || 
        (activity.type == ActivityType.TimeOffRequest && this.activityService.activityFilter == 'TimeOff') || isActivityPartOfMultiSelectFilter) {
        let subject = this.getActivitysubject(activity);

        if (activity.type === ActivityType.Sample) {
          mappedObject.text = subject;
          mappedObject.contactName = (activity as SampleActivity).contactName;
        }
        if (activity.type === ActivityType.TimeOffRequest) {
          mappedObject.text = isEqual(mappedObject.text, "Time Off") ? this.translate.instant("TIME_OFF") : mappedObject.text;
          mappedObject.allDay = (<TimeOffActivity>activity).totIsAlldayEvent;
          if (mappedObject.allDay) {
            mappedObject.startDate = new Date(new Date(activity.scheduledStart).setHours(0,0,0,0));
            mappedObject.endDate = new Date(new Date(activity.scheduledEnd).setHours(23,59,59,0));
            const startDateTimeValue: Date = mappedObject.startDate;
            const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.startTime = meetingStartTime;
            const endDateTimeValue: Date =mappedObject.endDate;
            const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.endTime = meetingEndTime;
          }
        }
        if (activity.type === ActivityType.TimeOff) {
          mappedObject.text = isEqual(mappedObject.text, "Time Off") ? this.translate.instant("TIME_OFF") : mappedObject.text;
          mappedObject.allDay = activity.allDayEvent != undefined ? activity.allDayEvent : (activity as TimeOffActivity).totIsAlldayEvent;
          if (mappedObject.allDay) {
            mappedObject.startDate = new Date(new Date(activity.scheduledStart).setHours(0,0,0,0));
            mappedObject.endDate = new Date(new Date(activity.scheduledEnd).setHours(23,59,59,0));
            const startDateTimeValue: Date = mappedObject.startDate;
            const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.startTime = meetingStartTime;
            const endDateTimeValue: Date =mappedObject.endDate;
            const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.endTime = meetingEndTime;
          }
        }
        if (activity.type === ActivityType.FollowUp) {
          mappedObject.text = isEqual(mappedObject.text, "Follow-up Action") ? this.translate.instant("FOLLOW_UP_ACTION") : mappedObject.text;
          //Untill we have a date only field for due date, update start and end time manually
          mappedObject.allDay = true;
          mappedObject.startDate = new Date(new Date(activity.scheduledStart).setHours(0,0,0,0));
          if (this.dateTimeFormatsService.isValidDate(activity.scheduledEnd)) {
            mappedObject.endDate = new Date(new Date(activity.scheduledEnd).setHours(23,59,59,0));
          } else {
            mappedObject.endDate = new Date(new Date(activity.scheduledStart).setHours(23,59,59,0));
            mappedObject.end = mappedObject.endDate;
          }

          const startDateTimeValue: Date = mappedObject.startDate;
          const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
          mappedObject.startTime = meetingStartTime;
          const endDateTimeValue: Date =mappedObject.endDate;
          const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
          mappedObject.endTime = meetingEndTime;
        }
        if(activity.type === ActivityType.PhoneCall){
          mappedObject.text = subject;
          mappedObject.selectedMobileNumber = (activity as PhoneActivity).selectedMobileNumber;;
        }
        if (activity.type === ActivityType.CaseIntake) {
          if(activity instanceof CaseActivity) {
            let contact = activity._case_contact;
            let contactName = contact ? contact.fullName : (activity._case_trans_customer_name && activity._case_trans_customer_name != Utility.globalCustomerText) ? activity._case_trans_customer_name : this.translate.instant('NO_GLOBALECUSTOMER', { globalCustomerText: this.utilityService.globalCustomerText });
            let status = this.activityService.getCaseState(activity);
            mappedObject.text = subject;
            mappedObject.info = status +"   " +contactName;
          }
        }
        if (activity.type === ActivityType.MedicalInquiry) {
          if (activity instanceof MedicalInquiry) {
            const status = (activity as MedicalInquiry).statusCodeFormatted || '';
            const contactName = (activity as MedicalInquiry).contactFormatted || '';
            mappedObject.text = subject;
            mappedObject.info = status && contactName ? `${status}   ${contactName}` : (status || contactName);
          }
        }
        if (activity.type === ActivityType.Order) {
          mappedObject.text = subject;
          mappedObject.location = ((activity as OrderActivity).accountId) ? (activity as OrderActivity).accountNameString : this.translate.instant('NO_ACCOUNT');
        }
        if (activity.type === ActivityType.SurgeryOrder) {
          mappedObject.text = activity.subject;
          mappedObject.account = this.activityService.getSecondaryInfoProcedureLog(activity);
        }
        if (activity.type === ActivityType.ProcedureTracker) {
          mappedObject.text = activity.subject;
          mappedObject.account = this.activityService.getSecondaryInfoProcedureTracker(activity);
        }
        if (activity.type === ActivityType.Email) {
          mappedObject.text = subject
          if (activity instanceof EmailActivity) {
            let filteredStartDateTime: Date = activity.scheduledStart;
            if ((activity.emailStatus == EmailStatusCodes.Sent || activity.emailStatus == EmailStatusCodes.Failed || activity.emailStatus == EmailStatusCodes.Shared)
              && activity.emailActivityParties && activity.emailActivityParties.length) {
              let sentDateList: number[] = [];
              if(activity.channelType == ChannelType.SMS || activity.channelType == ChannelType.WHATSAPP || activity.channelActivityType == ChannelActivityType.SMS || activity.channelActivityType == ChannelActivityType.WHATSAPP
                || activity.channelType.toLowerCase().includes('sms') || activity.channelType.toLowerCase().includes('whatsapp')) {
                  activity.emailActivityParties.forEach(epa => {
                  if (epa.email_senton) {
                    sentDateList.push(new Date(parseFloat(epa.email_senton.toString())).getTime());
                  }
                });
              } else {
                activity.emailActivityParties.forEach(epa => {
                  if (epa.emailAddresses) {
                    sentDateList.push(...epa.emailAddresses.map(ea => ea.email_senton && new Date(parseFloat(ea.email_senton.toString())).getTime()));
                  }
                });
              }
              if (sentDateList.length > 0) {
                const minSentDate = Math.min(...sentDateList);
                if (minSentDate) filteredStartDateTime = new Date(minSentDate);
              }
            }
            mappedObject.startDate = new Date(new Date(filteredStartDateTime));
            mappedObject.endDate = this.dateTimeFormatsService.addMinutesToDate(new Date(filteredStartDateTime), 30);
            mappedObject.start = new Date(mappedObject.startDate);
            mappedObject.end = new Date(mappedObject.endDate);
            mappedObject.recommended = activity['indskr_recommendedmessage'];
            if (activity['indskr_recommendedmessage'] && activity.channelType === ChannelType.WECOM && activity.emailStatus == EmailStatusCodes.Draft) {
              mappedObject.allDay = true;
            } 
            const startDateTimeValue: Date = mappedObject.startDate;
            const meetingStartTime = startDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.startTime = meetingStartTime;
            const endDateTimeValue: Date =mappedObject.endDate;
            const meetingEndTime = endDateTimeValue.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' });
            mappedObject.endTime = meetingEndTime;
          }
        }
        if (activity.type === ActivityType.SetBooking) {
          mappedObject.text = subject;
          mappedObject.location = this.secInfoConfigDataService.isKitBookingSecInfoConfigured
              ? this.secInfoConfigDataService.getSecondaryInfoFormatedTextForKitBooking(activity as SetBookingActivity, SecondaryInfoEntityName.Kitbooking)
              : activity.statusString;
          mappedObject.tooltip = this.secInfoConfigDataService.isKitBookingSecInfoConfigured
          ? this.secInfoConfigDataService.getSecondaryInfoFormatedTextForKitBooking(activity as SetBookingActivity, SecondaryInfoEntityName.Kitbooking)
          : mappedObject.text;
        }
        if(activity.type !== ActivityType.SetBooking)
          mappedObject.tooltip = mappedObject.text;
        mappedObject.__activity = activity;
        return mappedObject;
      }
      //Condition for Joint Meeting/ Remote Meeting
      if (activity.type === ActivityType.Appointment && (this.activityService.activityFilter === ActivityType.JointMeeting || this.activityService.activityFilter === ActivityType.RemoteMeeting)) {
        mappedObject.tooltip = mappedObject.text;
        mappedObject.__activity = activity;
        return mappedObject;
      } else if (activity.type === ActivityType.PhoneCall && this.activityService.activityFilter === ActivityType.JointPhoneCall) {
        //Condition for Joint PhoneCalls
        mappedObject.text = activity.subject;
        mappedObject.tooltip = mappedObject.text;
        mappedObject.selectedMobileNumber = (activity as PhoneActivity).selectedMobileNumber;
        mappedObject.__activity = activity;
        return mappedObject;
      }
    }

    return undefined;
  }

  getActivitysubject(activity: Activity) {

    let subject = ''
    switch (activity.type) {
      case ActivityType.Appointment:
        subject = ((activity && activity.subject) ? (activity.subject == 'Meeting' ? this.translate.instant('MEETING') : this.authService.hasFeatureAction(FeatureActionsMap.VISIT_AUTO_SUBJECT) && activity.subject == 'Visit' ? this.translate.instant('VISIT') :activity.subject) + '' : '')
        break;
      case ActivityType.StoreCheck:
        subject = activity?.subject == 'Store Check' ? this.translate.instant('STORE_CHECK') : activity?.subject;
        break;
      case ActivityType.Sample:
        if (activity instanceof SampleActivity) {
          if (activity.isPromoMaterial) {
            subject = activity.contactName
              ? `${this.translate.instant('PROMOTIONAL_MATERIAL')} - ${activity.contactName}`
              : this.translate.instant('PROMOTIONAL_MATERIAL');
          } else if (activity.subject) {
            subject = activity.subject.includes('Order') ? activity.subject.replace('Order', this.translate.instant('ALLOWCATION_ORDER')) : activity.subject;
          }
        }
        break;
      case ActivityType.TimeOffRequest:
        //OMNI-6073
        let timeoffRequestActivity = <TimeOffActivity>activity;

        subject = (timeoffRequestActivity && timeoffRequestActivity.name) ? (timeoffRequestActivity.name == 'Time Off' ? this.translate.instant('TIME_OFF') : timeoffRequestActivity.name) : this.translate.instant('TIME_OFF');

        break;
      case ActivityType.TimeOff:
        let timeoffActivity = <TimeOffActivity>activity;
        subject = (timeoffActivity && timeoffActivity.name) ? (timeoffActivity.name == 'Time Off' ? this.translate.instant('TIME_OFF') : timeoffActivity.name) : this.translate.instant('TIME_OFF');
        break;
      case ActivityType.Email:
        subject = this.setPrimaryTextRightForMessages(activity as EmailActivity);
        break;
      case ActivityType.FollowUp:
        subject = (activity && activity.subject) ? (activity.subject === 'Follow-up Action' ? this.translate.instant('FOLLOW_UP_ACTION') : activity.subject) : this.translate.instant('FOLLOW_UP_ACTION')
        break;
      case ActivityType.Order:
        subject =(activity && activity.subject) ? (activity.subject.includes(this.translate.instant('NEW_ACTIVITY_SALES_ORDER')) ?  activity.subject : this.translate.instant('NEW_ACTIVITY_SALES_ORDER') + ' ' + activity.subject) : this.translate.instant('NEW_ACTIVITY_SALES_ORDER');
        break;
      case ActivityType.CaseIntake:
        if(activity instanceof CaseActivity) {
           subject = activity.caseName;
          let contact = activity._case_contact;
          let contactName = contact ? contact.fullName : (activity._case_trans_customer_name && activity._case_trans_customer_name != Utility.globalCustomerText) ? activity._case_trans_customer_name : `${this.translate.instant('NO')} ${this.utilityService.globalCustomerText}`;
          let status = activity.state === 1 ? 'Resolve' : activity.state === 2 ? 'Cancelled' : activity._case_stage_value ? activity._case_stage_value : 'Open';
        }
        break;
      case ActivityType.SurgeryOrder:
        subject = activity.subject
        break;
      case ActivityType.MedicalInquiry:
        subject = (activity as MedicalInquiry).displayTitle || this.translate.instant('MEDICAL_INQUIRY');
        break;
      case ActivityType.PhoneCall:
        let phonecallSubject = activity.subject;
        subject = (activity && activity.subject) && (activity.subject == 'Phone Call' || activity.subject.includes(' - Phone Call') ? phonecallSubject.replace('Phone Call', this.translate.instant('NEW_ACTIVITY_PHONECALL')) : phonecallSubject);
        break;
      case ActivityType.SetBooking:
        subject = activity.subject;
        break;
      default:
        subject = '';
        break;
    }
    return subject;
  }

  public setPrimaryTextRightForMessages(activity: EmailActivity): string {
    let channelNameText: string = this.setSecondaryTextForMessages(activity) || '';
    if (isEmpty(activity?.emailActivityParties)) {
      return channelNameText;
    } else {
      let fullName;
      if (activity.emailActivityParties && activity.emailActivityParties.length == 1) {
        if (activity.emailActivityParties[0]?.contact_firstname) {
          fullName = this.getContactName(activity.emailActivityParties[0]);
          return `${fullName} - ${channelNameText}`;
        } else return channelNameText + '';
      } else if (activity.emailActivityParties && activity.emailActivityParties.length >= 2) {
        let cntName = 0;
        let cntNameStr = '';
        if (activity.emailActivityParties[0]?.contact_firstname) {
          fullName = this.getContactName(activity.emailActivityParties[0]);;
        }
        for (let emailActivityParty in activity.emailActivityParties) {
          if (activity.emailActivityParties[emailActivityParty]?.contact_firstname) {
            cntName = cntName + 1;
          }
        }
        cntNameStr = cntName == 0 ? '' : String(cntName = cntName - 1);
        return `${fullName} + ${cntNameStr} - ${channelNameText}`;
      } else return channelNameText + '';
    }
  }

  private getContactName(emailParty: EmailActivityParty) {
    return `${emailParty.contact_firstname} ${isEmpty(emailParty.contact_lastname) ? '' : emailParty.contact_lastname}`;
  }

  public setSecondaryTextForMessages(activity: EmailActivity): string {
    const channel: Channel = this.consentService.savedChannels.find(sch => sch.indskr_consenttypeid === activity.indskr_channelid);
    let channelTypeText;
    if (channel != undefined) {
      channelTypeText = String(channel?.indskr_consentType);
    } else if(activity.channelName){
      channelTypeText = activity.channelName;
    }
     else {
      channelTypeText = activity.channelType;
    }
    return channelTypeText ? ((channelTypeText === 'Email') ? this.translate.instant('EMAIL') : channelTypeText) : channelTypeText;
  }

  public scrollToToday(): void {
    const today = new Date();
    this.selectedDate = today;
    this.masterSelectedDate = today;
    if (this.calendar) {
      this.calendar.navigate(today);
    }
  }

  onCalendarPageLoaded(event: any): void {
    const startDate = event.firstDay;
    const endDate = event.lastDay;
    
    // Guard: Only process if the visible range has actually changed
    if (this.lastVisibleStart && this.lastVisibleEnd) {
      const startChanged = this.lastVisibleStart.getTime() !== startDate.getTime();
      const endChanged = this.lastVisibleEnd.getTime() !== endDate.getTime();
      
      if (!startChanged && !endChanged) {
        // Dates haven't changed, skip processing to prevent infinite loop
        return;
      }
    }
    
    this.lastVisibleStart = startDate;
    this.lastVisibleEnd = endDate;
    this.buildSkeletonActivities(startDate, endDate);
  }

  public getVisibleRange() {
    return { start: this.lastVisibleStart, end: this.lastVisibleEnd };
  }
}

